import { MENU_SECTIONS } from '@/utils/config/menuSections'
import { placementTypes } from '@/utils/placements/adapters'
import placementsRegistry from '@/utils/placements/registry'
import i18n from '@/utils/plugins/i18n'

export * from '@/utils/placements/adapters'
export { registerWidgetType, resolveWidgetType, getWidgetTypes } from '@/utils/placements/widgets'

const sortByOrder = (placements) => _.orderBy(placements, [({ order = 0 }) => order, 'id'], ['asc', 'asc'])

const getTranslatedLabel = ({ labels, locale }) => {
  if (_.isString(labels)) return labels
  if (!_.isPlainObject(labels)) return null

  const fallbackLabel = labels.en || _.head(_.values(labels))
  return labels[locale] || fallbackLabel || null
}

const normalizePlacement = (placement) => {
  if (!_.isPlainObject(placement)) return null

  const normalizedPlacement = _.cloneDeep(placement)

  if (!_.isPlainObject(normalizedPlacement.widget) || !_.isString(normalizedPlacement.widget.type)) {
    console.warn(
      `[placements] Placement "${normalizedPlacement.id}" is missing a valid widget.type.`,
    )
  }

  return normalizedPlacement
}

const findPlacementIndex = ({ id }) => {
  if (!id) return -1
  return placementsRegistry.findIndex((placement) => _.get(placement, 'id') === id)
}

export const getPlacements = () => {
  if (!_.isArray(placementsRegistry)) return []
  return placementsRegistry.filter((placement) => _.isPlainObject(placement))
}

export const registerPlacement = (placement, { replace = true } = {}) => {
  const normalizedPlacement = normalizePlacement(placement)
  if (!normalizedPlacement?.id) return null

  const existingPlacementIndex = findPlacementIndex({ id: normalizedPlacement.id })

  if (existingPlacementIndex === -1) {
    placementsRegistry.push(normalizedPlacement)
    return normalizedPlacement
  }

  if (!replace) return placementsRegistry[existingPlacementIndex]

  placementsRegistry.splice(existingPlacementIndex, 1, normalizedPlacement)
  return normalizedPlacement
}

export const registerPlacements = (placements, options) => {
  if (!_.isArray(placements)) return []
  return placements.map((placement) => registerPlacement(placement, options)).filter(Boolean)
}

export const unregisterPlacement = ({ id } = {}) => {
  const index = findPlacementIndex({ id })
  if (index === -1) return null

  const [removedPlacement] = placementsRegistry.splice(index, 1)
  return removedPlacement || null
}

const getSideMenuPlacements = () => {
  return sortByOrder(getPlacements())
    .filter(({ type }) => type === placementTypes.sideMenuPage)
    .filter(({ meta }) => meta?.scope === 'org')
    .filter(({ meta }) => !_.isNil(meta?.raw?.id))
}

export const getPlacementMenuSections = ({ orgCanonical }) => {
  const locale = i18n.global.locale.value

  return getSideMenuPlacements().map((placement) => {
    const widgetId = String(placement.meta.raw.id)
    const widgetSlug = placement.widgetSlug || widgetId
    const label = placement.widget?.config?.title
      || getTranslatedLabel({ labels: placement.labels, locale })
      || `Widget ${widgetSlug}`

    return {
      // Stable per-widget identifier for the v-for :key in CyMainNav. The route name
      // (`name`) is shared by all widget items, so we cannot use it as a key.
      id: `pluginWidget:${widgetSlug}`,
      name: 'pluginWidget',
      routeName: 'pluginWidget',
      label,
      icon: placement.widget?.config?.icon || 'extension',
      params: { orgCanonical, widgetSlug },
      order: placement.order ?? 0,
      widgetSlug,
    }
  })
}

export const getComponentTabPlacements = () =>
  getPlacements()
    .filter(({ type }) => type === placementTypes.componentTab)
    .filter(({ meta }) => meta?.scope === 'component')
    .filter(({ meta }) => _.isFinite(Number(meta?.raw?.id)))
    .slice()
    .sort((a, b) => Number(a.meta.raw.id) - Number(b.meta.raw.id))

export const resolveRouteLabel = ({ routeName }) => {
  const routeKey = `routes.${routeName}`
  const translated = i18n.global.te(routeKey) ? i18n.global.t(routeKey) : null
  return translated || routeName
}

/**
 * Build the ordered list of nav sections (native + plugin) for a given org,
 * applying saved positions from the navConfig store and a stable default order
 * so that native sections always precede plugin widgets when no config is saved.
 *
 * @param {object} opts
 * @param {string} opts.orgCanonical
 * @param {(type: string, key: string) => number} opts.positionFor - positionFor getter from navConfig store
 * @param {object} [opts.sectionView={}] - per-section icon/action overrides (used by CyMainNav)
 */
export const buildNavSections = ({ orgCanonical, positionFor, sectionView = {} }) => {
  const nativeSections = MENU_SECTIONS.map((section, index) => ({
    ...section,
    label: resolveRouteLabel({ routeName: section.name }),
    ...sectionView[section.name],
    position: positionFor('native', section.name),
    defaultOrder: index,
  }))
  const pluginSections = getPlacementMenuSections({ orgCanonical }).map((section, index) => ({
    ...section,
    position: positionFor('plugin_widget', section.widgetSlug),
    defaultOrder: MENU_SECTIONS.length + index,
  }))
  return _.orderBy([...nativeSections, ...pluginSections], ['position', 'defaultOrder'], ['asc', 'asc'])
}
